import { addDays, startOfDay } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Modal } from '@ui/components/Modal'
import { Calendar } from '@ui/v2/Calendar/Calendar'

import type { DxTradeOrderType } from '@model/DxTrade.types'

import { Box } from '@shared/components/Box'

import { FIX_TIME_IN_FORCE } from '../../utils/constants'
import { formatExpireDateUtc, mapOrderTypeToFixOrdType } from '../../utils/helpers'
import type { OrderCancelReplaceParams } from '../hooks/useFixTradingWebSocket'
import type { PendingOrderWithLots } from '../hooks/usePendingOrdersWithLots'
import { sideMapping } from '../utils/mapping'

interface ValidTillModalProps {
  order: PendingOrderWithLots
  accountCode?: string
  onOrderExpireDateChange?: (newExpireDate: string | null) => void
  replaceOrder?: (
    params: OrderCancelReplaceParams,
    onSuccess?: () => void,
    onSettled?: () => void
  ) => string
}

export const ValidTillModal: BaseModalType<ValidTillModalProps> = (props) => {
  const { close, order, accountCode, onOrderExpireDateChange, replaceOrder } = props
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()

  const expireDateValue = order.expireDate ? new Date(order.expireDate) : null

  const minCancelDate = startOfDay(addDays(new Date(), 1))

  const [selectedDate, setSelectedDate] = useState<Date | null>(expireDateValue)
  const [isLoading, setIsLoading] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date)
  }

  const buildFixReplaceParamsBase = () => {
    const leg = order.legs?.[0]
    const price = leg?.price ?? undefined
    const quantity = typeof leg?.quantity === 'number' ? leg.quantity : undefined
    const symbol = order.instrument ?? undefined
    const side = sideMapping(order.side)
    const account = order.account ?? (accountCode ? `default:${accountCode}` : undefined)
    const origClOrdID = order.clientOrderId

    if (!symbol || !side || !account || !origClOrdID) {
      addSnackbar.error({ message: t('errors.Something went wrong') })
      return undefined
    }

    return { price, quantity, symbol, side, account, origClOrdID }
  }

  const handleConfirm = () => {
    if (!replaceOrder) {
      addSnackbar.error({ message: t('errors.Something went wrong') })
      return
    }
    const base = buildFixReplaceParamsBase()
    if (!base) return

    const { price, quantity, symbol, side, account, origClOrdID } = base

    const expireDate = formatExpireDateUtc(selectedDate)
    const ordType = mapOrderTypeToFixOrdType(order.type as DxTradeOrderType | undefined)

    setIsLoading(true)
    replaceOrder(
      {
        origClOrdID,
        listId: account,
        account,
        symbol,
        side,
        orderQty: quantity,
        ordType,
        timeInForce: FIX_TIME_IN_FORCE.GOOD_TILL_DATE,
        expireDate,
        price,
        isExpiryUpdate: true,
      },
      undefined,
      () => {
        if (isMountedRef.current) {
          setIsLoading(false)
          close()
        }
        onOrderExpireDateChange?.(expireDate ?? null)
      }
    )
  }

  const handleClear = () => {
    if (!order.expireDate) {
      setSelectedDate(null)
      return
    }
    if (!replaceOrder) {
      addSnackbar.error({ message: t('errors.Something went wrong') })
      return
    }
    const base = buildFixReplaceParamsBase()
    if (!base) return

    const { price, quantity, symbol, side, account, origClOrdID } = base
    const ordType = mapOrderTypeToFixOrdType(order.type as DxTradeOrderType | undefined)

    setIsLoading(true)
    replaceOrder(
      {
        origClOrdID,
        listId: account,
        account,
        symbol,
        side,
        orderQty: quantity,
        ordType,
        timeInForce: FIX_TIME_IN_FORCE.DAY,
        price,
        isExpiryUpdate: true,
      },
      undefined,
      () => {
        if (isMountedRef.current) {
          setIsLoading(false)
          close()
        }
        onOrderExpireDateChange?.(null)
      }
    )
  }

  return (
    <Modal>
      <Modal.Header canClose>{t('Portfolio.Cancellation date')}</Modal.Header>
      <Modal.Body>
        <Box fullWidth>
          <Calendar
            selected={selectedDate ?? undefined}
            onChange={handleDateChange}
            minDate={minCancelDate}
            monthsShown={1}
          />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={handleClear} disabled={!selectedDate || isLoading}>
          {t('Clear')}
        </Modal.Footer.Secondary>
        <Modal.Footer.Primary
          onClick={handleConfirm}
          disabled={!selectedDate || isLoading}
          loading={isLoading}
        >
          {t('Confirm')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
